import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { firstValueFrom, Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { ExtendedUser, UserCreationStatus } from '../../../models';
import { User } from '../../../models/domain';
import {
  CreateUserAction,
  LoadUsersAction,
  RemoveUserAction,
  ResendUserInvitationAction,
  UsersAdapterActions,
} from '../../../store/actions';
import { UserSelectors } from '../../../store/selectors';
import { ActionDispatcherService, OperationError } from '@anecdotes/shared-infra';
import { TrackOperations } from '../../operations-tracker/constants/track.operations.list.constant';
import { AuthService, RoleEnum } from '@anecdotes/auth-core';
import { Dictionary } from '@ngrx/entity';

@Injectable()
export class UserFacadeService {
  private byEmailInCache: { [mail: string]: User } = {};

  constructor(
    private store: Store,
    private actionDispatcher: ActionDispatcherService,
    private authService: AuthService
  ) {}

  static getUserFullName(user: User): string {
    return UserFacadeService.getFullName(user.first_name, user.last_name);
  }

  static getFullName(firstName?: string, lastName?: string): string {
    const parts = [firstName, lastName].filter((part) => !!part);

    return parts.join(' ');
  }

  static getNamePartsFromFullName(fullName: string): { firstName: string; lastName?: string } {
    const [firstName, ...lastNameParts] = fullName.split(' ');
    const lastName = lastNameParts.join(' ') || undefined;
    return { firstName, lastName };
  }

  async initAsync(): Promise<void> {
    if (!await firstValueFrom(this.store.select(UserSelectors.SelectIsInitialized))) {
      await this.actionDispatcher.dispatchActionAsync(new LoadUsersAction(), TrackOperations.LOAD_USERS);
    }
  }

  isLoading(): Observable<boolean> {
    return this.store.pipe(select(UserSelectors.SelectIsLoading));
  }

  async createUserAsync(
    newUser: User,
    service_id?: string,
    service_instance_id?: string,
    note?: string,
  ): Promise<UserCreationStatus> {
    const notification: Record<string, string> = {};

    if (service_id) {
      notification.service_id = service_id;
    }

    if (service_instance_id) {
      notification.service_instance_id = service_instance_id;
    }

    if (note) {
      notification.note = note;
    }

    try {
      await this.actionDispatcher.dispatchActionAsync(
        new CreateUserAction({
          user: newUser,
          notification: Object.keys(notification).length ? notification : null,
        }),

        TrackOperations.CREATE_USER,
      );
      return UserCreationStatus.created;
    } catch (err) {
      if (err instanceof OperationError) {
        if (err.errorCode === 409) {
          return UserCreationStatus.alreadyExist;
        }
      }

      return UserCreationStatus.unknownError;
    }
  }

  resendInvitationAsync(email: string): Promise<void> {
    return this.actionDispatcher.dispatchActionAsync(
      new ResendUserInvitationAction(email),
      email,
      TrackOperations.UPDATE_USER,
    );
  }

  changeUserRoleAsync(email: string, newRoleKey: string): Promise<void> {
    return this.actionDispatcher.dispatchActionAsync(
      UsersAdapterActions.changeRole({ email, newRoleKey: newRoleKey }),
      email,
      UsersAdapterActions.changeRole.type
    );
  }

  changeAuditorPermissionsAsync(email: string, auditFrameworks: string[]): Promise<void> {
    return this.actionDispatcher.dispatchActionAsync(
      UsersAdapterActions.changeAuditorPermissions({ email, auditFrameworks }),
      email,
      UsersAdapterActions.changeAuditorPermissions.type
    );
  }

  toggleEmailLogin(email: string): void {
    this.store.dispatch(UsersAdapterActions.toggleEmailLogin({ email }));
  }

  getUsersDictionary(): Observable<Dictionary<ExtendedUser>> {
    return this.store.select(UserSelectors.SelectCalculatedUsersDictionary).pipe(filter(Boolean));
  }

  getUsers(): Observable<ExtendedUser[]> {
    return this.getUsersDictionary().pipe(map((usersDictionary) => Object.values(usersDictionary)));
  }

  getAdminsAndCollaborators(): Observable<User[]> {
    return this.getUsers().pipe(
      map((users: User[]) => {
        return users.filter(({ role }) => role === RoleEnum.Admin || role === RoleEnum.Collaborator);
      })
    );
  }

  getCurrentUser(): Observable<User> {
    return this.authService.getUser().pipe(
      switchMap((userClaims) => {
        return this.getUser(userClaims.user_id);
      })
    );
  }

  getCurrentUserEmail(): Observable<string> {
    return this.getCurrentUser().pipe(map((user) => user.email));
  }

  getUser(userId: string): Observable<User> {
    return this.getUsers().pipe(map((users) => users.find((user) => user.uid === userId)));
  }

  getUserByEmail(userEmail: string): Observable<User> {
    return this.getUsersDictionary().pipe(map((usersDictionary) => usersDictionary[userEmail]));
  }

  removeUser(user_email: string): Promise<void> {
    return this.actionDispatcher.dispatchActionAsync(
      new RemoveUserAction({ email: user_email }),
      user_email,
      TrackOperations.REMOVE_USER,
    );
  }

  auditorsExist(framework_id: string): Observable<boolean> {
    return this.getUsers().pipe(
      map((users) => users.some((user) => user.audit_frameworks.includes(framework_id))),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  getFrameworkAuditors(frameworkId: string): Observable<User[]> {
    return this.getUsers().pipe(
      map((users: User[]) =>
        users.filter((user) => user.role === RoleEnum.Auditor && user.audit_frameworks.includes(frameworkId))
      )
    );
  }

  hasFrameworkAuditors(frameworkId: string): Observable<boolean> {
    return this.getFrameworkAuditors(frameworkId).pipe(map((auditors: User[]) => !!auditors.length));
  }

  setUsersDictionaryByEmail(users: User[]): void {
    users.forEach((user) => {
      this.byEmailInCache[user.email] = user;
    });
  }

  getUserByEmailFromDictionary(userEmail: string): User {
    if (userEmail in this.byEmailInCache) {
      return this.byEmailInCache[userEmail];
    }
    return null;
  }

  getUsersByEmailFromDictionary(usersEmails: string[]): (User | string)[] {
    const users: (User | string)[] = [];
    usersEmails.forEach((email) => {
      if (email in this.byEmailInCache) {
        users.push(this.byEmailInCache[email]);
      } else {
        users.push(email);
      }
    });
    return users;
  }
}
